import React, { useContext } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { useThrottle } from '@wix/da-hooks/pkg/useThrottle';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import Plus from '@wix/da-ds/pkg/Icons/Plus';
import Check from '@wix/da-ds/pkg/Icons/24x24/Check';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import { BiEventType, ReferralType } from '@wix/da-bi/pkg/constants';
import { useRestrictToLoggedInAndVerified } from '../utils/hooks/useRestrictToLoggedInAndVerified';
import { ResourceType } from '../types/resource';
import s from './JoinGroupButton.scss';
const JoinGroupButton = ({ group, requestJoin, className, showAdminButton, type = 'text', size })=>{
    const { t } = useTranslation();
    const isMobile = useContext(MobileContext);
    // throttle those button presses so there's time for any notifications to disappear/backend requests to complete
    const requestJoinThrottled = useThrottle(()=>requestJoin(group), [
        requestJoin,
        group
    ], 6000);
    const { isLoggedIn, isVerificationNeeded, restrictToLoggedInAndVerified } = useRestrictToLoggedInAndVerified();
    const loggedInAndVerified = isLoggedIn && !isVerificationNeeded;
    const biData = loggedInAndVerified ? {
        evid: BiEventType.JOIN_GROUP,
        groupid: group.useridUuid,
        sectionname: 'button'
    } : undefined;
    const onClick = restrictToLoggedInAndVerified(ReferralType.JOIN_GROUP, 'joingroup', requestJoinThrottled);
    const { isAdmin, isIngroup } = group.extendedGroup || {};
    if (isAdmin) {
        var _group_extendedGroup;
        if (showAdminButton) {
            return /*#__PURE__*/ React.createElement(GhostButton, {
                size: size || 'medium',
                variant: "approval-green",
                className: className,
                href: Url.groupNotificationsLink(group.userId),
                biData: {
                    evid: BiEventType.VIEW_GROUP_NOTIFICATION,
                    typeid: ResourceType.GROUP,
                    itemid: group.userId,
                    groupid: group.useridUuid,
                    sectionname: 'button'
                }
            }, t('joinGroupButton.admin'));
        }
        return /*#__PURE__*/ React.createElement(GhostButton, {
            size: size || 'small',
            variant: "main",
            className: className,
            icon: Check,
            disabled: true
        }, ((_group_extendedGroup = group.extendedGroup) === null || _group_extendedGroup === void 0 ? void 0 : _group_extendedGroup.rolelabel) || 'Admin');
    }
    if (isMobile) {
        if (isIngroup) {
            return /*#__PURE__*/ React.createElement(TextButton, {
                size: size || 'small',
                variant: "main",
                className: className,
                icon: Check,
                iconClassName: s['check-icon'],
                disabled: true,
                noHorizontalPaddings: false
            }, t('joinGroupButton.member'));
        }
        return /*#__PURE__*/ React.createElement(SolidButton, {
            variant: "approval-green",
            size: size || 'small',
            className: className,
            onClick: onClick,
            biData: biData
        }, t('joinGroupButton.join'));
    }
    if (type === 'text') {
        if (isIngroup) {
            return /*#__PURE__*/ React.createElement(TextButton, {
                size: size || 'small',
                variant: "main",
                className: className,
                icon: Check,
                iconClassName: s['check-icon'],
                disabled: true,
                noHorizontalPaddings: false
            }, t('joinGroupButton.member'));
        }
        return /*#__PURE__*/ React.createElement(TextButton, {
            size: size || 'small',
            variant: "approval-green",
            className: classnames(s['join-text-btn'], className),
            onClick: requestJoinThrottled,
            icon: Plus,
            iconClassName: s['plus-icon'],
            biData: {
                evid: BiEventType.JOIN_GROUP,
                groupid: group.useridUuid
            },
            noHorizontalPaddings: false
        }, t('joinGroupButton.join'));
    }
    if (isIngroup) {
        return /*#__PURE__*/ React.createElement(SolidButton, {
            size: size || 'medium',
            variant: "main",
            className: className,
            icon: Check,
            disabled: true
        }, t('joinGroupButton.member'));
    }
    return /*#__PURE__*/ React.createElement(SolidButton, {
        size: size || 'medium',
        variant: "approval-green",
        className: className,
        onClick: requestJoinThrottled,
        biData: biData
    }, t('joinGroupButton.join'));
};
JoinGroupButton.displayName = 'JoinGroupButton';
export default JoinGroupButton;
